import { Children, cloneElement, useContext, useMemo, useState } from 'react';

import { useDashboardGetQuery } from '../../Common/Queries/dashboardQueries';
import { useInterviewsGetQuery } from '../../Common/Queries/interviewsQueries';

import { isValidID } from '../../utils';
import { getAllInterviewMap } from '../../utils/responseMap.ts';

import { AuthContext } from '../../constants';

export const DashboardDataContainer = ({ children }) => {
  const { userInfo } = useContext(AuthContext) || {};
  const userId = isValidID(userInfo?.entityID) && userInfo?.entityID;
  const dashboard = useDashboardGetQuery();

  const [monthsPast, setMonthsPast] = useState(6);
  const [monthsFuture, setMonthsFuture] = useState(6);

  const loadMorePast = () => setMonthsPast((prev) => prev + 6);
  const loadMoreFuture = () => setMonthsFuture((prev) => prev + 6);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const startTimeFrom = useMemo(() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() - monthsPast);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  }, [today, monthsPast]);

  const startTimeToToday = useMemo(() => {
    const d = new Date(today);
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  }, [today]);

  const startTimeFromToday = useMemo(() => {
    const d = new Date(today);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  }, [today]);

  const startTimeToFuture = useMemo(() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() + monthsFuture);
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  }, [today, monthsFuture]);

  const upcomingInterviewParams = useMemo(
    () => ({
      page: 1,
      limit: 1000,
      interviewerID: userId,
      view: 'upcoming-interviews',
      sort: 'ASC',
      orderBy: 'startTime',
      'startTimeRange.start-time': startTimeFromToday,
      'startTimeRange.end-time': startTimeToFuture,
    }),
    [userId, startTimeFromToday, startTimeToFuture]
  );
  const upcomingInterviews = useInterviewsGetQuery(upcomingInterviewParams, {
    select: getAllInterviewMap,
    enabled: Boolean(userId),
  });
  const pastInterviewParams = useMemo(
    () => ({
      page: 1,
      limit: 1000,
      interviewerID: userId,
      view: 'previous-interviews',
      sort: 'DESC',
      orderBy: 'startTime',
      'startTimeRange.start-time': startTimeFrom,
      'startTimeRange.end-time': startTimeToToday,
    }),
    [userId, startTimeFrom, startTimeToToday]
  );
  const pastInterviews = useInterviewsGetQuery(pastInterviewParams, {
    select: getAllInterviewMap,
    enabled: Boolean(userId),
  });
  const child = Children.only(children);

  return cloneElement(child, {
    dashboard,
    upcomingInterviews,
    pastInterviews,
    monthsPast,
    monthsFuture,
    loadMorePast,
    loadMoreFuture,
  });
};
